import { useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Components.
import EChart from './chart/echarts/EChart';
import { EChartClickParams } from './chart/echarts/echartsCore';
import { buildPieOption, PiePoint, toPiePoint } from './chart/echarts/pieOption';

// Types.
import ChartOption from '../types/instance/ChartOption';
import { FilterType } from '../typescript/Enums';
import FilterOption from '../types/instance/FilterOption';

// Contexts
import { TimeRangeContext } from "../context/TimeRangeContext";
import { buildApiQuery } from "../helpers/timeRangeUtils";
import { useIsInStatementSidePanel } from "../context/StatementSidePanelContext";
import ConditionalRender from "../helpers/ConditionalRender";

// Components
import ChatButton from "./chatGPT/ChatButton";
import Prompts from "./chatGPT/Prompts";

type PieChartProps = {
    filtersOptions: FilterOption[],
    uniqueIndex: number,
    chartOptions: ChartOption[],
    defaultChartOption: ChartOption,
    setFilterOptions: Function,
    instanceType?: string,
    onSliceClick?: (point: any, chartKey: string) => void,
    title?: string,
    hideOptions?: boolean,
    loading?: boolean,
    customTooltipFormatter?: (point: any) => string
};

function PieChart(props: PieChartProps) {
    const timeRangeContext = useContext(TimeRangeContext)
    const navigate = useNavigate();
    const isInStatementSidePanel = useIsInStatementSidePanel();

    // Create a unique localStorage key for this pie chart instance
    const storageKey = `pieChart_${props.uniqueIndex}_selectedOption`;

    const [chartOption, setChartOption] = useState<ChartOption>(props.defaultChartOption);

    // Handle loading saved preference and updating chart option
    useEffect(() => {
        try {
            const savedOptionKey = localStorage.getItem(storageKey);
            if (savedOptionKey && props.chartOptions.length > 0) {
                // Check if saved option is still valid with current chartOptions
                const savedOption = props.chartOptions.find(option => option.key === savedOptionKey);
                if (savedOption) {
                    // Use saved option if it exists and has data
                    setChartOption(savedOption);
                    return;
                } else {
                    // Saved option no longer exists, clear it from storage
                    localStorage.removeItem(storageKey);
                }
            }

            // Fall back to default option
            setChartOption(props.defaultChartOption);

        } catch (error) {
            console.warn('Failed to handle chart option update:', error);
            setChartOption(props.defaultChartOption);
        }
    }, [props.defaultChartOption, props.chartOptions, storageKey]);

    function setOption(chosenOption: string) {

        let chartOptions = props.chartOptions.filter(chartOption => chartOption.key === chosenOption);

        if (chartOptions.length > 0) {

            // Requested chart option found.
            setChartOption(chartOptions[0]);

            // Save the selection to localStorage
            try {
                localStorage.setItem(storageKey, chosenOption);
            } catch (error) {
                console.warn('Failed to save chart option to localStorage:', error);
            }
        }
    }

    const handleSliceClick = (point: PiePoint) => {
        // Prevent default handler for non-filterable charts like "Label"
        if (typeof props.onSliceClick === "function" || chartOption.key === "Label") {
            if (typeof props.onSliceClick === "function") {
                props.onSliceClick(point, chartOption.key);
            }
            // Do nothing for "Label" chart
            return;
        }

        // TODO Find a common mechanism for all click events.
        function shouldRedirect(name: string): boolean {
            switch (name) {
                case 'Batches':
                case 'Statements':
                case 'Grouped Batches':
                case 'Grouped Statements':
                    return true;

                default:
                    return false;
            }
        }

        function getFilterType(name: string): FilterType {
            switch (name) {
                case 'Batches':
                    // return FilterType.Batches;
                    break;

                case 'Clients':
                    return FilterType.Clients;

                case 'Schemas':
                    return FilterType.Databases;

                case 'Databases':
                    return FilterType.Databases;

                case 'Programs':
                    return FilterType.Programs;

                case 'Statements':
                    // return FilterType.Statements;
                    break;

                case 'Sessions':
                    return FilterType.Sessions;

                case 'Users':
                    return FilterType.Users;

                case 'Waits':
                    return FilterType.Waits;

                default:
                    break;
            }

            throw new Error
            (`No equivalent filter type for ${name}.`);
        }

        if (shouldRedirect(chartOption.key)) {
            const pathname: string = `${point.url}/activity`;
            const search: string = point.queryParameters ?
                    point.queryParameters :
                    buildApiQuery(timeRangeContext.timeRange);
            navigate({ pathname, search });
        } else {
            const filterType: FilterType = getFilterType(chartOption.key);
            // @ts-ignore
            let options: FilterOption[] = props.filtersOptions;

            // TODO Find out why options becomes empty...
            if (options.length <= filterType) {
                options =
                        [
                            {
                                filterType: FilterType.Waits,
                                filters: []
                            },
                            {
                                filterType: FilterType.Databases,
                                filters: []
                            },
                            {
                                filterType: FilterType.Sessions,
                                filters: []
                            },
                            {
                                filterType: FilterType.Clients,
                                filters: []
                            },
                            {
                                filterType: FilterType.Users,
                                filters: []
                            },
                            {
                                filterType: FilterType.Programs,
                                filters: []
                            },
                            {
                                filterType: FilterType.Modules,
                                filters: []
                            }
                        ];
            }

            const filters: string[] =
                    options[filterType].filters;

            // Only allow one filter of each type
            if (filters.length === 0 &&
                    point.name !== '-') {
                filters.push(point.name);
                props.setFilterOptions(options);
            }
        }
    };

    const clickRef = useRef(handleSliceClick);
    clickRef.current = handleSliceClick;

    const onChartClick = useCallback((params: EChartClickParams) => clickRef.current(toPiePoint(params)), []);

    const tooltipFormatterRef = useRef(props.customTooltipFormatter);
    tooltipFormatterRef.current = props.customTooltipFormatter;
    const hasCustomTooltip = !!props.customTooltipFormatter;

    const pieOption = useMemo(() => buildPieOption({
        slices: chartOption.data.map((slice: any) => ({
            name: slice.name,
            value: slice.y,
            color: slice.color,
            formatted: slice.formatted,
            url: slice.url,
            queryParameters: slice.queryParameters,
        })),
        tooltipFormatter: hasCustomTooltip
            ? (point) => tooltipFormatterRef.current!(point)
            : undefined,
    }), [chartOption.data, hasCustomTooltip]);


    if (props.chartOptions.length === 0) {

        // No chart options passed through.
        return (
                <div className="col">
                    <div className="card collapsible">
                        <div className="card-header">
                            Missing required properties
                            <div className="btn-group float-end">
                                <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                                   data-bs-target=".multi-collapse-pie" aria-expanded="false" aria-controls=""></i>
                            </div>
                        </div>
                        <div id={`collapseChart${props.uniqueIndex}`}
                             className="card-body collapse show multi-collapse-pie">
                            No chart options have been passed through - please check component properties and ensure valid
                            objects are getting passed through.
                        </div>
                    </div>
                </div>
        )
    }

    const getTopWaits = () => {
        const waits = props.chartOptions.find(option => option.key === 'Waits');
        if (!waits || !Array.isArray(waits.data)) return [];
        const sortedData = waits.data.sort((a, b) => b.y - a.y);
        // Calculate the total time
        const totalTime = sortedData.reduce((total, item) => total + item.y, 0);

        // Calculate 80% of the total time
        const eightyPercentTotal = 0.8 * totalTime;

        // Find the top items whose total time sums 80% of the total
        let cumulativeSum = 0;
        let topItems = [];
        for (let item of sortedData) {
            cumulativeSum += item.y;
            topItems.push(item);
            if (cumulativeSum >= eightyPercentTotal) {
                break;
            }
        }
        return topItems.map(item => item.name).slice(0, 8);
    }
    const topWaitsList = getTopWaits()

    return (
            <div className="col">
                <div className="card collapsible">
                    <div className="card-header d-flex align-items-center justify-content-between">
                        <div>
                            <i className="fal fa-chart-pie-alt fa-fw" aria-hidden="true"></i>
                            <span className="ms-1">{props.title ? props.title : `Top ${chartOption.key.toLowerCase()}`}</span>
                        </div>
                        <div>
                            <ConditionalRender if={props.instanceType && chartOption.key === 'Waits' && topWaitsList.length}>
                                <ChatButton
                                    value={Prompts.getInstanceWaitsPrompt(topWaitsList, props.instanceType)}
                                    label={isInStatementSidePanel ? 'Ask AI Co-pilot' : undefined}
                                />
                            </ConditionalRender>
                            <ConditionalRender if={!props.hideOptions}>
                                <button id="btnGroupDrop1" type="button"
                                        className="btn btn-xsm btn-primary dropdown-toggle ms-1" data-bs-toggle="dropdown"
                                        aria-expanded="false">
                                    {chartOption.key} Chart
                                </button>
                                <ul className="dropdown-menu border" aria-labelledby="btnGroupDrop1">
                                    {props.chartOptions.map((option: ChartOption, index) => (
                                            <li key={index}>
                                                {option.key === chartOption.key ? (
                                                        <span className="dropdown-item active" role="button"
                                                              onClick={() => setOption(option.key)}>{option.key}</span>
                                                ) : (
                                                        <span className="dropdown-item" role="button"
                                                              onClick={() => setOption(option.key)}>{option.key}</span>
                                                )}
                                            </li>
                                    ))}
                                </ul>
                            </ConditionalRender>
                            <i className="collapse-toggle" role="button" data-bs-toggle="collapse"
                               data-bs-target=".multi-collapse-pie" aria-expanded="false" aria-controls=""></i>
                        </div>

                    </div>

                    <div id={`collapseChart${props.uniqueIndex}`} className="card-body collapse show multi-collapse-pie">
                        <EChart option={pieOption} height={300} onClick={onChartClick}/>
                    </div>

                </div>
            </div>
    );
}

export default PieChart;
